import localizeCurrency from "@helpers/localizeCurrency"
import localizeDate from "@helpers/localizeDate"
import { PaymentIntent } from "@stripe/stripe-js"
import { addMonths, addWeeks, addYears } from "date-fns"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { Plan } from "src/types"

interface OrderSummaryProps {
  fullPrice: number
  paymentIntent: PaymentIntent | undefined
  plan: Plan
}

export default function OrderSummary({
  fullPrice,
  paymentIntent,
  plan,
}: OrderSummaryProps) {
  const { t } = useTranslation()

  const renewalDate = useMemo(() => {
    switch (plan) {
      case "weekly":
        return addWeeks(new Date(), 1)
      case "monthly":
        return addMonths(new Date(), 1)
      case "yearly":
        return addYears(new Date(), 1)
    }
  }, [plan])

  return (
    <div className="space-y-3">
      <h2 className="text-lg font-bold">
        {t("pages.checkout.payment.orderSummary.orderSummary")}
      </h2>

      <div className="space-y-4 rounded-2xl border border-gray-200 bg-white p-6 shadow-xs">
        <div className="space-y-1 text-sm">
          <p className="font-semibold text-black">
            {t("pages.checkout.payment.orderSummary.fullaccessHeadline")}
          </p>

          <p className="text-gray-500">
            {t("pages.checkout.payment.orderSummary.fullaccessContent")}
          </p>
        </div>

        <div className="space-y-1 text-sm">
          <p className="font-semibold text-black">
            {t("pages.checkout.payment.orderSummary.durationHeadline")}
          </p>

          <p className="text-gray-500">
            {plan === "weekly"
              ? t("pages.checkout.payment.orderSummary.durationWeek", {
                  count: 1,
                })
              : plan === "monthly"
                ? t("pages.checkout.payment.orderSummary.durationMonth", {
                    count: 1,
                  })
                : t("pages.checkout.payment.orderSummary.durationYear", {
                    count: 1,
                  })}
          </p>
        </div>

        <div className="space-y-1 text-sm">
          <p className="font-semibold text-black">
            {t("pages.checkout.payment.orderSummary.renewalHeadline")}
          </p>

          <p className="text-gray-500">{localizeDate(renewalDate)}</p>

          <p className="text-xs text-gray-400">
            {t("pages.checkout.payment.orderSummary.renewalDisclaimer")}
          </p>
        </div>

        {paymentIntent && (
          <div className="space-y-1 border-t border-gray-100 pt-4">
            {paymentIntent.amount !== fullPrice ? (
              <>
                <p className="text-sm text-gray-400 line-through">
                  {t(
                    `pages.checkout.payment.${plan === "weekly" ? "thisWeek" : plan === "monthly" ? "thisMonth" : "thisYear"}`,
                    {
                      price: localizeCurrency({
                        amountInCents: fullPrice,
                        currency: paymentIntent.currency,
                      }),
                    },
                  )}
                </p>

                <p className="font-bold text-black">
                  {t(
                    plan === "weekly"
                      ? "pages.checkout.payment.firstWeekSpecialOffer"
                      : plan === "monthly"
                        ? "pages.checkout.payment.firstMonthSpecialOffer"
                        : "pages.checkout.payment.firstYearSpecialOffer",
                    {
                      price: localizeCurrency({
                        amountInCents: paymentIntent.amount,
                        currency: paymentIntent.currency,
                      }),
                    },
                  )}
                </p>
              </>
            ) : (
              <p className="font-bold text-black">
                {t(
                  `pages.checkout.payment.${plan === "weekly" ? "thisWeek" : plan === "monthly" ? "thisMonth" : "thisYear"}`,
                  {
                    price: localizeCurrency({
                      amountInCents: paymentIntent.amount,
                      currency: paymentIntent.currency,
                    }),
                  },
                )}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
